import React from "react";
import { Pagination } from "@mui/material";
import { useTranslation } from "react-i18next";
import { PaginationResponse } from "@redux/api/internalApiSlice";
import { TABLE_PAGE_SIZE_OPTIONS, usePagination } from "@helpers/utils";
import { basePaginationSx } from "@reusables/BasePagination";
import BaseDropdown from "@reusables/dropdowns/BaseDropdown";

type PageSizeOption = {
    id: number;
    label: string;
};

type BaseTablePaginationControlsProps = {
    pagination: ReturnType<typeof usePagination>;
    total?: PaginationResponse<any> | number;
    className?: string;
};

const pageSizeOptions: PageSizeOption[] = TABLE_PAGE_SIZE_OPTIONS.map((size) => ({
    id: size,
    label: String(size),
}));

export default function BaseTablePaginationControls({
    pagination,
    total,
    className = "",
}: BaseTablePaginationControlsProps) {
    const { t } = useTranslation();
    const paginationProps = pagination.adapt(total);

    return (
        <div className={`${className} flex flex-wrap items-center justify-end gap-[12px]`}>
            <span className="whitespace-nowrap text-[14px] font-medium text-accent">
                {t("general.pagination.rows")}
            </span>

            <div className="w-[124px] min-w-[124px]">
                <BaseDropdown<PageSizeOption>
                    options={pageSizeOptions}
                    value={pagination.limit}
                    getter={{
                        key: (opt) => opt.id,
                        label: (opt) => opt.label,
                    }}
                    onChange={(newItem) => {
                        if (newItem) {
                            pagination.setLimit(newItem.id);
                        }
                    }}
                    disableClearable
                    contentPlacement="top"
                    customize={{
                        padding: "4px 12px",
                    }}
                    sx={{
                        "& input": {
                            fontVariantNumeric: "tabular-nums",
                        },
                    }}
                />
            </div>

            <Pagination {...paginationProps} shape="rounded" sx={basePaginationSx} />
        </div>
    );
}
